import styled, { css } from 'styled-components'

/* ---------- 主题变量 ---------- */
const THEME = {
  primary: '#1fc7d4',
  primaryDark: '#12a5b1',
  primarySoft: 'rgba(31, 199, 212, 0.12)',
  primaryGlow: 'rgba(31, 199, 212, 0.35)',
  ink: '#0b1a1d',
  inkSoft: '#41575b',
  line: 'rgba(11, 26, 29, 0.07)',
  surface: 'rgba(255, 255, 255, 0.78)',
  alert: '#e5484d',
}

/* ---------- 容器 ---------- */

export const NavBar = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  height: 67px;

  background: ${THEME.surface};
  backdrop-filter: saturate(200%) blur(24px);
  -webkit-backdrop-filter: saturate(200%) blur(24px);

  border-bottom: 1px solid ${THEME.line};
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.6) inset,
    0 1px 2px rgba(11, 26, 29, 0.02),
    0 18px 40px -24px rgba(11, 26, 29, 0.18);

  transition: background 0.35s ease, box-shadow 0.35s ease, height 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${THEME.primary} 20%,
      ${THEME.primaryDark} 50%,
      ${THEME.primary} 80%,
      transparent 100%
    );
    opacity: 0.85;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: 60px;
  }
`
export const NavBarInner = styled.div`
  width: 100%;
  max-width: 1600px;
  height: 100%;
  margin: 0 auto;
  padding: 0 12px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 18px;
  }
`
/* ---------- 品牌 / 移动端触发 ---------- */

export const Brand = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  flex-shrink: 0;
  padding-right: 8px;

  /* Logo 后面的主题色柔光底衬 */
  &::after {
    content: '';
    position: absolute;
    left: -14px;
    top: 50%;
    width: 56px;
    height: 56px;
    transform: translateY(-50%);
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${THEME.primaryGlow} 0%,
      transparent 70%
    );
    opacity: 0.55;
    filter: blur(6px);
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  img {
    position: relative;
    z-index: 1;
    height: 36px;
    width: auto;
    object-fit: contain;
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  &:hover::after {
    opacity: 0.9;
  }

  &:hover img {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`

export const MobileTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  transition: background 0.25s ease;

  svg {
    width: 26px;
    height: 26px;
    color: ${THEME.ink};
    transition: transform 0.25s ease, color 0.25s ease;
  }

  &:hover {
    background: ${THEME.primarySoft};

    svg {
      color: ${THEME.primaryDark};
      transform: scale(1.06);
    }
  }
`

/* ---------- 导航菜单 ---------- */

export const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  height: 100%;
  margin-left: 56px;

  @media (max-width: 1200px) {
    margin-left: 32px;
    gap: 4px;
  }

  @media (max-width: 1024px) {
    margin-left: 16px;
    gap: 2px;
  }
`

/* 单个导航项 —— 胶囊式悬浮，主题色渐变下划线 */
export const NavLink = styled.div<{ $tone?: 'alert' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.012em;
  color: ${THEME.ink};
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.22s ease, background 0.22s ease;

  /* 底部细线，hover 时从中心展开 */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 2px;
    width: 22px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, ${THEME.primary}, ${THEME.primaryDark});
    transform: translateX(-50%) scaleX(0);
    transform-origin: center;
    opacity: 0;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.24s ease;
  }

  &:link,
  &:visited {
    color: ${THEME.ink};
  }

  &:hover {
    color: ${THEME.primaryDark};
    background: ${THEME.primarySoft};

    &::after {
      transform: translateX(-50%) scaleX(1);
      opacity: 1;
    }

    & > ul,
    & > div[data-dropdown] {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, 0);
      pointer-events: auto;
    }
  }

  ${({ $tone }) =>
    $tone === 'alert' &&
    css`
      color: ${THEME.alert};

      &:link,
      &:visited {
        color: ${THEME.alert};
      }

      &::after {
        background: ${THEME.alert};
      }

      &:hover {
        color: #cf2f34;
        background: rgba(229, 72, 77, 0.1);
      }
    `}
`

export const NavLabel = styled.span<{ $active?: boolean }>`
  position: relative;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: inherit;
  transition: font-weight 0.2s ease, color 0.2s ease;
`

/* ---------- 下拉面板 ---------- */

export const DropdownPanel = styled.div.attrs({ 'data-dropdown': true })`
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translate(-50%, 10px);
  min-width: 220px;
  padding: 10px;
  border-radius: 18px;

  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: saturate(180%) blur(28px);
  -webkit-backdrop-filter: saturate(180%) blur(28px);

  border: 1px solid ${THEME.line};
  box-shadow:
    0 2px 6px rgba(11, 26, 29, 0.04),
    0 28px 56px -18px rgba(11, 26, 29, 0.2),
    0 0 0 4px rgba(31, 199, 212, 0.06);

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 0.28s ease,
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    visibility 0.28s ease;

  &::before {
    content: '';
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
  }
`

/* ---------- 右侧操作区 ---------- */

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  flex-shrink: 0;
  padding-left: 12px;

  @media (max-width: 768px) {
    gap: 10px;
    padding-left: 0;
  }
`